/**
 * Preview環境専用の認証サービス
 *
 * @description
 * Preview環境でのみ動作するクイックログイン機能を提供します。
 * 本番環境では完全に無効化され、セキュリティリスクはありません。
 *
 * @security
 * - Preview環境のみ有効化（二重環境チェック）
 * - LocalStorageにモックデータのみ保存
 * - 実際の認証トークンは含まれない
 *
 * @see Issue #485
 */

/**
 * Preview環境専用ユーザー型
 */
import type { TenantContext } from '../types/multiTenant';
import logger from '../utils/logger';

export interface PreviewUser {
  id: string;
  email: string;
  name: string;
  role: 'owner' | 'staff';
  isPreview: true;
  tenantId?: string;
  shopName?: string;
}

/**
 * Preview環境認証サービス
 */
export class PreviewAuthService {
  private static readonly STORAGE_KEY = 'ripia_preview_user';

  /**
   * Preview環境または開発環境（DEV_LOGIN有効）かどうかを判定
   *
   * @returns {boolean} Preview環境または開発環境の場合true
   */
  private static isPreviewEnvironment(): boolean {
    const mode = import.meta.env.MODE;
    const vercelEnv = import.meta.env.VITE_VERCEL_ENV;
    const enableDevLogin = import.meta.env.VITE_ENABLE_DEV_LOGIN;

    // 二重チェック: 実際のproduction環境では絶対に無効化
    // NOTE: Vercel builds ALL environments with MODE=production, so only check VITE_VERCEL_ENV
    if (vercelEnv === 'production') {
      return false;
    }

    // Preview環境または開発環境（DEV_LOGIN有効）の場合true
    return (
      vercelEnv === 'preview' ||
      (mode === 'development' && enableDevLogin === 'true')
    );
  }

  /**
   * Previewユーザーを設定
   *
   * @param {PreviewUser} user - 設定するPreviewユーザー
   * @throws {Error} 本番環境で実行された場合
   */
  static setPreviewUser(user: PreviewUser): void {
    // セキュリティチェック: 実際の本番環境では例外をスロー
    // NOTE: Vercel builds ALL environments with MODE=production, so only check VITE_VERCEL_ENV
    const vercelEnv = import.meta.env.VITE_VERCEL_ENV;
    const mode = import.meta.env.MODE;
    const enableDevLogin = import.meta.env.VITE_ENABLE_DEV_LOGIN;

    if (vercelEnv === 'production') {
      throw new Error(
        'PreviewAuthService.setPreviewUser() is not available in production environment'
      );
    }

    // Preview環境または開発環境以外では警告
    if (!this.isPreviewEnvironment()) {
      logger.warn(
        '[PreviewAuthService] setPreviewUser is only available in preview/dev environment. Current environment:',
        { mode, vercelEnv, enableDevLogin }
      );
      return;
    }

    // LocalStorageに保存
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(user));
      logger.info('[PreviewAuthService] Preview user set:', user.role);
    } catch (error) {
      logger.error('[PreviewAuthService] Failed to set preview user:', error);
    }
  }

  /**
   * Previewユーザーを取得
   *
   * @returns {PreviewUser | null} 保存されているPreviewユーザー、存在しない場合null
   */
  static getPreviewUser(): PreviewUser | null {
    // Preview環境以外ではnullを返す
    if (!this.isPreviewEnvironment()) {
      return null;
    }

    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) {
        return null;
      }

      const user = JSON.parse(stored) as PreviewUser;

      // 型チェック
      if (
        !user.id ||
        !user.email ||
        !user.name ||
        !user.role ||
        !user.isPreview
      ) {
        logger.warn(
          '[PreviewAuthService] Invalid preview user data, clearing...'
        );
        this.clearPreviewUser();
        return null;
      }

      return user;
    } catch (error) {
      logger.error('[PreviewAuthService] Failed to get preview user:', error);
      return null;
    }
  }

  /**
   * Previewユーザーをクリア
   */
  static clearPreviewUser(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      logger.info('[PreviewAuthService] Preview user cleared');
    } catch (error) {
      logger.error('[PreviewAuthService] Failed to clear preview user:', error);
    }
  }

  /**
   * オーナーとしてログイン
   *
   * @returns {PreviewUser} オーナー権限のPreviewユーザー
   */
  static createOwnerUser(): PreviewUser {
    return {
      id: 'preview-owner-' + Date.now(),
      email: 'owner@preview.ripia.dev',
      name: 'Preview Owner',
      role: 'owner',
      isPreview: true,
      tenantId: 'preview-org-1',
      shopName: 'Preview Salon',
    };
  }

  /**
   * スタッフとしてログイン
   *
   * @returns {PreviewUser} スタッフ権限のPreviewユーザー
   */
  static createStaffUser(): PreviewUser {
    return {
      id: 'preview-staff-' + Date.now(),
      email: 'staff@preview.ripia.dev',
      name: 'Preview Staff',
      role: 'staff',
      isPreview: true,
      tenantId: 'preview-org-1',
      shopName: 'Preview Salon',
    };
  }

  /**
   * Preview環境用のTenantContextを作成
   *
   * @param {PreviewUser} user - PreviewUser
   * @returns {TenantContext} Preview用のTenantContext
   */
  static createPreviewTenantContext(user: PreviewUser): TenantContext {
    return {
      // Primary identifiers
      id: user.tenantId || 'preview-org-1',
      tenantId: user.tenantId || 'preview-org-1',
      name: user.shopName || 'Preview Salon',
      tenantName: user.shopName || 'Preview Salon',
      subdomain: 'preview',
      tenantSlug: 'preview',

      // Plan and features
      planType: 'standard',
      plan: 'standard',
      features: [
        'basic-booking',
        'customer-management',
        'staff-management',
        'analytics',
        'api-access',
      ],
      billingStatus: 'active',

      // User role in tenant
      userId: user.id,
      userRole: user.role,
      tenantRole: user.role,

      // Permissions (basic permissions for preview)
      permissions: {},

      // Store context
      currentStoreId: 'preview-store-1',
      availableStores: [
        {
          id: 'preview-store-1',
          name: `${user.shopName || 'Preview Salon'} 本店`,
          code: 'MAIN001',
        },
      ],
    };
  }
}
